/*
 * File: UpdatePeriodEnrollmentNotes.ts
 * Author: Austin Robinson
 * Date Created: 11/4/2025
 * Copyright (c) DrivingDoc 2025
 */
import type { SerializedEditorState } from 'lexical';
import { createMutation } from 'react-query-kit';

import { privateApi } from 'api';
import { invalidateQueryKeys } from 'api/hooks/middlewares';

export type UpdatePeriodEnrollmentNotesParams = {
  studentId: string;
  periodId: string;
  notes: SerializedEditorState;
};

export type UpdatePeriodEnrollmentNotesResult = {
  student_user_id: string;
  course_period_id: string;
  notes: SerializedEditorState;
  notes_updated_at: string;
  notes_updated_by: string;
};

async function updatePeriodEnrollmentNotes(
  params: UpdatePeriodEnrollmentNotesParams,
): Promise<UpdatePeriodEnrollmentNotesResult> {
  // make api call
  const result = await privateApi.patch(
    `${import.meta.env.VITE_API_BASE_URL}/courses/periods/enrollments/notes`,
    params,
  );

  // return the data object
  return result.data;
}

const useUpdatePeriodEnrollmentNotes = createMutation({
  mutationFn: async (variables: UpdatePeriodEnrollmentNotesParams) => updatePeriodEnrollmentNotes(variables),
  use: [
    invalidateQueryKeys<UpdatePeriodEnrollmentNotesResult, UpdatePeriodEnrollmentNotesParams>((_data, variables) => [
      // Invalidate all course periods to update the calendar
      { queryKey: ['course_periods'] },
      // Invalidate the specific course period to update the Drive Overview Dialog
      { queryKey: ['course_period', { id: variables.periodId }] },
      // Invalidate student's period enrollments to update Drive History section
      { queryKey: ['student_course_periods', { id: variables.studentId }] },
    ]),
  ],
});

export { updatePeriodEnrollmentNotes, useUpdatePeriodEnrollmentNotes };
